import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Fund, UnitClass } from '../../data/types';
import { compactMoney, date, DEMO_TODAY, money, unitPrice } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';
import PriceImportDialog from './PriceImportDialog';

function PricePanel({ unitClass }: { unitClass: UnitClass }) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [price, setPrice] = useState(unitClass.unitPrice.toFixed(4));
  const [effective, setEffective] = useState('2026-10-01');
  const [importing, setImporting] = useState(false);
  const data = unitClass.priceHistory
    .slice(-18)
    .map((point) => ({ date: point.date.slice(0, 7), price: point.price }));
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#64748B' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={(value: number) => value.toFixed(3)}
              tick={{ fontSize: 11, fill: '#64748B' }}
              width={52}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip formatter={(value: number) => unitPrice(value)} />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="price"
              stroke="#4F46E5"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mt: 2 }}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          size="small"
          label="New unit price"
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="Effective"
          value={effective}
          onChange={(event) => setEffective(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button onClick={() => setImporting(true)}>Import prices</Button>
        <Button
          variant="outlined"
          onClick={() => {
            const value = Number(price);
            if (!Number.isFinite(value) || value <= 0) {
              toast('Enter a valid unit price', 'error');
              return;
            }
            dispatch({
              type: 'UNIT_PRICE_SET',
              classId: unitClass.id,
              date: effective,
              price: value,
            });
            toast(`Unit price ${unitPrice(value)} published from ${date(effective)}`);
          }}
        >
          Publish price
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        Prices come from the fund administrator&apos;s monthly NAV. Publishing revalues every
        holding and is used for applications, DRP and redemptions from the effective date.
      </Typography>
      <PriceImportDialog
        unitClass={unitClass}
        open={importing}
        onClose={() => setImporting(false)}
      />
    </Box>
  );
}

export default function FundClassesTab({ fund }: { fund: Fund }) {
  const scope = useTenantScope();
  const classes = scope.classes.filter((unitClass) => unitClass.fundId === fund.id);
  return (
    <Stack spacing={2}>
      {classes.map((unitClass) => {
        const holdings = scope.holdings.filter((holding) => holding.classId === unitClass.id);
        return (
          <SectionCard
            key={unitClass.id}
            title={`${unitClass.name} (${unitClass.code})`}
            subtitle={`${holdings.length} holders · ${compactMoney(sumBy(holdings, (holding) => holding.value))} on register`}
            action={
              <StatusChip
                status={unitClass.open ? 'OPEN' : 'CLOSED'}
                label={unitClass.open ? 'Open to applications' : 'Closed'}
              />
            }
          >
            <KeyValueGrid
              columns={4}
              items={[
                {
                  label: 'Unit price',
                  value: `${unitPrice(unitClass.unitPrice)} (as at ${date(DEMO_TODAY)})`,
                },
                { label: 'Issue price', value: unitPrice(unitClass.issuePrice) },
                { label: 'Target return', value: `${unitClass.targetReturnPct}% p.a.` },
                { label: 'Management fee', value: `${unitClass.managementFeePct}% p.a.` },
                { label: 'Minimum investment', value: money(unitClass.minInvestment) },
                {
                  label: 'Units on issue',
                  value: sumBy(holdings, (holding) => holding.units).toLocaleString('en-AU'),
                },
                {
                  label: 'Pricing',
                  value: fund.basis === 'NAV_UNITS' ? 'Monthly NAV' : 'Fixed at $1.0000',
                },
                {
                  label: 'DRP',
                  value: fund.basis === 'NAV_UNITS' ? 'Available, at NAV' : 'Not offered',
                },
              ]}
            />
            {fund.basis === 'NAV_UNITS' && <PricePanel unitClass={unitClass} />}
          </SectionCard>
        );
      })}
    </Stack>
  );
}
